import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Building, Calendar, MoreVertical, Pencil, X } from 'lucide-react';
import { ExperienceItem } from '@/hooks/useProfile';
import { cn } from '@/lib/utils';
import { calculateDuration, getShortDuration } from '@/utils/durationCalculator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface ExperienceItemCardProps {
  item: ExperienceItem;
  index: number;
  onRemove: (index: number) => void;
  onEdit?: (item: ExperienceItem, index: number) => void; 
  isNewlyAdded?: boolean;
  disabled?: boolean;
}

const ExperienceItemCard = ({ 
  item, 
  index, 
  onRemove, 
  onEdit,
  isNewlyAdded = false, 
  disabled = false 
}: ExperienceItemCardProps) => {
  // State to hold the duration, will be updated periodically for current positions
  const [duration, setDuration] = useState('');
  const [shortDuration, setShortDuration] = useState('');
  
  // Update duration for current positions to keep it accurate
  useEffect(() => {
    // Calculate initial duration
    const calculateAndSetDuration = () => {
      const calculatedDuration = calculateDuration(item.start_date, item.end_date, item.current || false);
      setDuration(calculatedDuration);
      setShortDuration(getShortDuration(calculatedDuration));
    };
    
    calculateAndSetDuration();
    
    // If this is a current position, set up an interval to update the duration
    let intervalId: number | null = null;
    if (item.current) {
      // Update once per day
      intervalId = window.setInterval(calculateAndSetDuration, 86400000);
    }
    
    // Clean up interval on component unmount
    return () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
    };
  }, [item.start_date, item.end_date, item.current]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString + '-01');
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  return (
    <Card 
      className={cn(
        "relative",
        isNewlyAdded && "animate-slide-in-right"
      )}
    >
      <div className="absolute top-2 right-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled={disabled}
            >
              <MoreVertical className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {onEdit && (
              <DropdownMenuItem
                onClick={() => onEdit(item, index)}
                disabled={disabled}
                className="flex items-center gap-2"
              >
                <Pencil className="h-4 w-4" />
                Edit
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => onRemove(index)}
              disabled={disabled}
              className="flex items-center gap-2 text-destructive focus:text-destructive"
            >
              <X className="h-4 w-4" />
              Remove
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      <CardContent className="p-4">
        <div className="grid gap-2">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="font-medium mb-1">{item.position}</div>
              <div className="text-sm flex items-center gap-1">
                <Building className="h-3 w-3" />
                {item.company}
                {item.location && <span>• {item.location}</span>}
              </div>
              <div className="text-sm flex items-center gap-1 mt-1">
                <Calendar className="h-3 w-3" />
                {formatDate(item.start_date)} — {item.current ? 'Present' : formatDate(item.end_date || '')}
                <span className="ml-1 text-xs px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded-full">
                  {duration}
                </span>
              </div>
            </div>
          </div>
          
          {item.description && (
            <div className="md:w-2/3 text-sm text-muted-foreground mt-2">
              {item.description}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ExperienceItemCard;
